"use client";

import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";

const ISSUE_PHOTOS_DIR = "flextraker/issues";

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Normalize path to relative format for Filesystem operations.
 * Handles legacy full URI paths (file:///...) and converts to relative paths.
 */
function normalizePath(path: string): string {
  // If it's a full URI, extract the relative path
  if (path.startsWith("file://")) {
    // Extract everything after "Documents/"
    const documentsMatch = path.match(/Documents\/(.+)$/);
    if (documentsMatch) {
      return documentsMatch[1];
    }
  }
  return path;
}

export async function captureIssuePhoto(source: CameraSource = CameraSource.Camera): Promise<string | null> {
  if (!isNativePlatform()) {
    console.warn("Issue photos only available on native platform");
    return null;
  }

  try {
    const photo = await Camera.getPhoto({
      quality: 80,
      resultType: CameraResultType.Base64,
      source: source,
      correctOrientation: true,
    });

    if (!photo.base64String) {
      throw new Error("No image data");
    }

    // Save to local filesystem
    const fileName = `issue-${Date.now()}.jpg`;
    const filePath = `${ISSUE_PHOTOS_DIR}/${fileName}`;

    await Filesystem.writeFile({
      path: filePath,
      data: photo.base64String,
      directory: Directory.Documents,
      recursive: true,
    });

    console.log("[IssuePhoto] Saved:", filePath);
    // Return relative path (not full URI) - matches map image pattern
    return filePath;
  } catch (error) {
    console.error("Failed to capture issue photo:", error);
    throw error;
  }
}

export async function loadIssuePhoto(path: string): Promise<string | null> {
  if (!isNativePlatform() || !path) return null;

  try {
    const normalizedPath = normalizePath(path);
    const result = await Filesystem.readFile({
      path: normalizedPath,
      directory: Directory.Documents,
    });

    if (typeof result.data === "string") {
      return `data:image/jpeg;base64,${result.data}`;
    }
    return null;
  } catch (error) {
    console.error("Failed to load issue photo:", error);
    return null;
  }
}

export async function deleteIssuePhoto(path: string): Promise<void> {
  if (!isNativePlatform() || !path) return;

  try {
    const normalizedPath = normalizePath(path);
    await Filesystem.deleteFile({
      path: normalizedPath,
      directory: Directory.Documents,
    });
    console.log("[IssuePhoto] Deleted:", normalizedPath);
  } catch (error) {
    console.warn("Failed to delete issue photo:", error);
  }
}
